import { type ReactNode } from "react";

export interface SettingsSectionProps {
  /** Section title */
  title: string;
  /** Optional section description */
  description?: string;
  /** Section content */
  children: ReactNode;
  /** Icon to display next to title */
  icon?: ReactNode;
  /** Color accent for the section */
  accent?: "magic" | "meadow" | "fairy" | "sunshine" | "sky";
}

/**
 * SettingsSection - A styled container for grouping related settings.
 *
 * Features a playful card design with accent colors and icons.
 *
 * @example
 * <SettingsSection title="Sound" icon={<SoundIcon />} accent="magic">
 *   <Toggle ... />
 *   <Slider ... />
 * </SettingsSection>
 */
export function SettingsSection({
  title,
  description,
  children,
  icon,
  accent = "magic",
}: SettingsSectionProps) {
  // Accent colors for the left border and icon background
  const accentStyles = {
    magic: {
      border: "border-l-magic-400",
      iconBg: "bg-magic-100",
      iconText: "text-magic-600",
      glow: "rgba(155, 89, 182, 0.1)",
    },
    meadow: {
      border: "border-l-meadow-400",
      iconBg: "bg-meadow-100",
      iconText: "text-meadow-600",
      glow: "rgba(107, 203, 119, 0.1)",
    },
    fairy: {
      border: "border-l-fairy-400",
      iconBg: "bg-fairy-100",
      iconText: "text-fairy-600",
      glow: "rgba(255, 107, 157, 0.1)",
    },
    sunshine: {
      border: "border-l-sunshine-400",
      iconBg: "bg-sunshine-100",
      iconText: "text-sunshine-700",
      glow: "rgba(255, 217, 61, 0.1)",
    },
    sky: {
      border: "border-l-sky-400",
      iconBg: "bg-sky-100",
      iconText: "text-sky-600",
      glow: "rgba(74, 144, 217, 0.1)",
    },
  };

  const styles = accentStyles[accent];

  return (
    <section
      className={`
        relative
        bg-white
        rounded-2xl
        border-2 border-neutral-200
        border-l-4 ${styles.border}
        shadow-[0_4px_16px_rgba(0,0,0,0.06),0_1px_4px_rgba(0,0,0,0.04)]
        overflow-hidden
        transition-all duration-300
        hover:shadow-[0_6px_20px_rgba(0,0,0,0.08),0_2px_6px_rgba(0,0,0,0.05)]
      `}
      style={{
        background: `linear-gradient(135deg, white 0%, white 95%, ${styles.glow} 100%)`,
      }}
    >
      {/* Header */}
      <div className="px-5 py-4 border-b border-neutral-100">
        <div className="flex items-center gap-3">
          {/* Icon circle */}
          {icon && (
            <div
              className={`
                w-10 h-10
                flex items-center justify-center
                rounded-xl
                ${styles.iconBg}
                ${styles.iconText}
              `}
            >
              {icon}
            </div>
          )}

          <div>
            <h2 className="font-display font-bold text-body-xl text-neutral-800">
              {title}
            </h2>
            {description && (
              <p className="font-body text-body-sm text-neutral-500 mt-0.5">
                {description}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 pr-6 space-y-4">{children}</div>
    </section>
  );
}

export default SettingsSection;
